
import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from "../../assets/logo-aft4.png";
import signature from "../../assets/Cachet et Signature AF4T.png";
import { COLORS, calculateDays, dateFormatter, priceFormatter, priceFormatterV2 } from '../../settings/themeSettings';


const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingVertical: 25,
        paddingHorizontal: 20,
        fontSize: 8,
        fontFamily: 'Helvetica'
    },
    headerRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12
    },
    logo: {
        height: 40,
        width: 90
    },
    voucherTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        color: COLORS.secondary,
        textTransform: 'uppercase',
        letterSpacing: 1
    },
    voucherBadge: {
        backgroundColor: COLORS.secondary,
        color: 'white',
        paddingHorizontal: 8,
        paddingVertical: 3,
        fontSize: 10,
        fontWeight: 'bold',
        borderRadius: 3,
        alignSelf: 'flex-start'
    },
    infoRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: 6,
        marginTop: 3
    },
    infoLabel: {
        fontSize: 8,
        textTransform: 'uppercase',
        color: '#555'
    },
    infoValue: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#000'
    },
    sectionTitle: {
        fontSize: 8,
        fontWeight: 'bold',
        paddingHorizontal: 6,
        paddingVertical: 3,
        backgroundColor: '#e0e0e0',
        color: '#333',
        marginTop: 10,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.5
    },
    tableRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        borderBottom: '0.5px solid #e0e0e0',
        paddingVertical: 3
    },
    tableHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        borderBottom: '1px solid black',
        paddingBottom: 3,
        marginBottom: 3
    },
    colDu:         { width: '12%', fontSize: 7.5 },
    colAu:         { width: '12%', fontSize: 7.5 },
    colHotel:      { width: '20%', fontSize: 7.5 },
    colRegime:     { width: '12%', fontSize: 7.5 },
    colPax:        { width: '7%',  fontSize: 7.5, textAlign: 'center' },
    colNuitee:     { width: '7%',  fontSize: 7.5, textAlign: 'center' },
    colType:       { width: '15%', fontSize: 7.5 },
    colCat:        { width: '15%', fontSize: 7.5 },
    divider: {
        height: 0.5,
        width: '100%',
        backgroundColor: '#ccc',
        marginVertical: 6
    }
});


const VoucherPDF = ({ voucher, dossier }) => {

    const dossierId = dossier?.reference
        ?? (voucher?.sejours?.length > 0 ? voucher.sejours[0].circuit?.reference : null)
        ?? '----';

    const circuit = voucher?.sejours?.length > 0 ? voucher.sejours[0].circuit : null;

    const sorted = [...(voucher?.sejours || [])].sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut));
    const computedPax = (circuit?.rooming?.length > 0) ? circuit.rooming.length :
                    ((circuit?.groups?.length > 0) ? circuit.groups.reduce((acc, g) => acc + (Number(g.pax) || 0), 0) :
                    (sorted.length > 0 ? (sorted[0].pax ?? circuit?.pax ?? '----') : (circuit?.pax ?? '----')));

    return <Document>

        <Page size="A5" style={styles.page}>

            {/* ── HEADER ─────────────────────────────────────── */}
            <View style={styles.headerRow}>
                <Image style={styles.logo} source={logo} />
                <View>
                    <Text style={{ ...styles.voucherTitle, backgroundColor: COLORS.secondary, color: 'white', paddingHorizontal: 10, paddingVertical: 4 }}>Réservation N° : {voucher?.numero ?? '----'}</Text>
                </View>
            </View>

            {/* ── INFO DOSSIER ────────────────────────────────── */}
            <View style={{ backgroundColor: '#f5f5f5', padding: 8, borderRadius: 3, marginBottom: 8 }}>
                <View style={styles.infoRow}>
                    <Text style={{ ...styles.infoLabel, fontSize: 9, color: COLORS.secondary }}>N° DOSSIER :</Text>
                    <Text style={{ ...styles.infoValue, fontSize: 9 }}>{dossierId}</Text>
                    <Text style={{ ...styles.infoLabel, marginLeft: 20 }}>Hôtel / Prestataire :</Text>
                    <Text style={styles.infoValue}>{voucher?.prestataire?.nom ?? '----'}</Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Nombre de pax :</Text>
                    <Text style={styles.infoValue}>{computedPax}</Text>
                    <Text style={{ ...styles.infoLabel, marginLeft: 20 }}>Client :</Text>
                    <Text style={styles.infoValue}>{circuit?.toClient?.libelle ?? '----'}</Text>
                </View>
            </View>

            {/* ── TABLEAU HÉBERGEMENTS ────────────────────────── */}
            <Text style={styles.sectionTitle}>Détail Hébergement</Text>

            <View>
                {/* En-têtes */}
                <View style={styles.tableHeader}>
                    <Text style={{ ...styles.colDu, fontWeight: 'bold' }}>Du</Text>
                    <Text style={{ ...styles.colAu, fontWeight: 'bold' }}>Au</Text>
                    <Text style={{ ...styles.colHotel, fontWeight: 'bold' }}>Prestataire</Text>
                    <Text style={{ ...styles.colRegime, fontWeight: 'bold' }}>Régime</Text>
                    <Text style={{ ...styles.colPax, fontWeight: 'bold', textAlign: 'center' }}>Pax</Text>
                    <Text style={{ ...styles.colNuitee, fontWeight: 'bold', textAlign: 'center' }}>Nuitées</Text>
                    <Text style={{ ...styles.colType, fontWeight: 'bold' }}>Type chambre</Text>
                    <Text style={{ ...styles.colCat, fontWeight: 'bold' }}>Catégorie</Text>
                </View>

                {/* Lignes */}
                {(voucher?.sejours || [])
                    .slice()
                    .sort((a, b) => new Date(a.dateDebut) - new Date(b.dateDebut))
                    .map((sejour, i) => (
                    <View key={i} style={styles.tableRow}>
                        <Text style={styles.colDu}>{sejour?.dateDebut ? dateFormatter(sejour.dateDebut) : ''}</Text>
                        <Text style={styles.colAu}>{sejour?.dateFin  ? dateFormatter(sejour.dateFin)  : ''}</Text>
                        <Text style={styles.colHotel}>{voucher?.prestataire?.nom ?? ''}</Text>
                        <Text style={styles.colRegime}>{sejour?.regime ?? ''}</Text>
                        <Text style={{ ...styles.colPax,    textAlign: 'center' }}>{sejour?.pax ?? ''}</Text>
                        <Text style={{ ...styles.colNuitee, textAlign: 'center' }}>{sejour?.nuitee ?? ''}</Text>
                        <Text style={styles.colType}>{sejour?.typeChambre ?? ''}</Text>
                        <Text style={styles.colCat}>{sejour?.categorieChambre ?? ''}</Text>
                    </View>
                ))}

                {/* Total */}
                {voucher?.sejours?.length > 0 && (
                    <View style={{ ...styles.tableRow, borderTop: '1px solid black', marginTop: 2, borderBottom: 'none' }}>
                        <Text style={{ ...styles.colDu }}></Text>
                        <Text style={{ ...styles.colAu }}></Text>
                        <Text style={{ ...styles.colHotel }}></Text>
                        <Text style={{ ...styles.colRegime, fontWeight: 'bold' }}>Total</Text>
                        <Text style={{ ...styles.colPax, textAlign: 'center' }}></Text>
                        <Text style={{ ...styles.colNuitee, textAlign: 'center', fontWeight: 'bold' }}>
                            {voucher.sejours.reduce((s, x) => s + (Number(x.nuitee) || 0), 0)}
                        </Text>
                        <Text style={{ ...styles.colType }}></Text>
                        <Text style={{ ...styles.colCat }}></Text>
                    </View>
                )}
            </View>

            {/* ── ROOMING ─────────────────────────────────────── */}
            <Text style={styles.sectionTitle}>Rooming</Text>

            {circuit?.isGroup ? (
                <View style={{ paddingHorizontal: 5 }}>
                    <View style={{ ...styles.tableHeader, marginTop: 4 }}>
                        <Text style={{ width: '50%', fontWeight: 'bold' }}>Libellé groupe</Text>
                        <Text style={{ width: '50%', fontWeight: 'bold', textAlign: 'center' }}>Pax</Text>
                    </View>
                    {circuit.groups?.map((room, i) => (
                        <View key={i} style={styles.tableRow}>
                            <Text style={{ width: '50%' }}>{room.libelle}</Text>
                            <Text style={{ width: '50%', textAlign: 'center' }}>{room.pax}</Text>
                        </View>
                    ))}
                </View>
            ) : (
                <View style={{ paddingHorizontal: 5 }}>
                    <View style={{ ...styles.tableHeader, marginTop: 4 }}>
                        <Text style={{ width: '15%', fontWeight: 'bold' }}>Civilité</Text>
                        <Text style={{ width: '42%', fontWeight: 'bold' }}>Nom</Text>
                        <Text style={{ width: '43%', fontWeight: 'bold' }}>Prénom</Text>
                    </View>
                    {circuit?.rooming?.map((room, i) => (
                        <View key={i} style={styles.tableRow}>
                            <Text style={{ width: '15%' }}>{room.civilite}</Text>
                            <Text style={{ width: '42%' }}>{room.nom}</Text>
                            <Text style={{ width: '43%' }}>{room.prenom}</Text>
                        </View>
                    ))}
                </View>
            )}

            {/* ── SIGNATURES ──────────────────────────────────── */}
            <View style={{
                marginTop: 30,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'flex-end'
            }}>
                {/* Signature prestataire */}
                <View style={{ width: '40%', textAlign: 'center' }}>
                    <View style={{ height: 40 }}></View>
                    <View style={{ borderTop: '1px solid black', paddingTop: 4 }}>
                        <Text style={{ fontSize: 7, textAlign: 'center' }}>Signature &amp; Cachet Prestataire</Text>
                    </View>
                </View>

                {/* Signature AFT — image électronique */}
                <View style={{ width: '45%', textAlign: 'center' }}>
                    <Image
                        style={{ height: 75, width: '100%', objectFit: 'contain' }}
                        source={signature}
                    />
                    <View style={{ borderTop: '1px solid black', paddingTop: 4 }}>
                        <Text style={{ fontSize: 7, textAlign: 'center' }}>Signature Électronique — Africa 4 Tourism</Text>
                    </View>
                </View>
            </View>

            {/* Pied de page */}
            <Text style={{ fontSize: 6, color: '#aaa', textAlign: 'center', marginTop: 12 }}>
                Ce document est un voucher officiel émis par Africa 4 Tourism — {new Date().toLocaleDateString('fr-FR')}
            </Text>

        </Page>

    </Document>;
};

export default VoucherPDF;
